/**
 * AccountingDashboardStore
 *
 * Manages data for the accounting dashboard view. Currently calculates metrics
 * from invoices, bills, and payments ServiceStores.
 *
 * TODO: When dashboard-metrics API is available, consider replacing calculateDashboardData()
 * with a call to the dashboard-metrics service for server-side calculated metrics.
 * See: /src/types/api-types/financial/dashboard-metrics/dashboard-metrics.types.ts
 *
 * The dashboardMetrics type provides pre-calculated metrics:
 * - revenue.mtd, revenue.ytd
 * - ar.outstanding, ar.overdue
 * - ap.outstanding, ap.overdue
 * - openPOs, pendingInvoices, cashPosition
 */
import { makeAutoObservable } from "mobx";
import type RootStore from "stores/RootStore";
import type ServiceStore from "stores/ServiceStore";
import type {
  Invoice,
  Bill,
  Payment,
  AccountingSummary,
  DashboardData,
} from "../types";
import { InvoiceStatus, BillStatus } from "../types";

/**
 * Pending approval item type
 */
export type ApprovalItemType = "invoice" | "bill" | "po" | "ica";

/**
 * Pending approval item interface
 */
export interface PendingApprovalItem {
  id: string;
  type: ApprovalItemType;
  number: string;
  description: string;
  amount: number;
  date: string;
  vendorOrCustomer: string;
  plantCode?: string;
  hasAttachments: boolean;
  attachmentCount: number;
  submittedBy: string;
  submittedAt: string;
  priority: "normal" | "high" | "urgent";
}
import { getDateRange, databaseDate, getOffsetDate } from '@intrasync/utils';
import AccountingScheduleLeftHeaderCell from "./AccountingScheduleCells/AccountingSchedule.LeftHeaderCell";
import AccountingScheduleLeftBodyCell from "./AccountingScheduleCells/AccountingSchedule.LeftBodyCell";
import AccountingScheduleRightHeaderCell from "./AccountingScheduleCells/AccountingSchedule.RightHeaderCell";
import AccountingScheduleRightBodyCell from "./AccountingScheduleCells/AccountingSchedule.RightBodyCell";

class AccountingDashboardStore {
  rootStore: RootStore;

  // Dashboard summary data
  summary: AccountingSummary = {
    totalReceivables: 0,
    totalPayables: 0,
    overdueReceivables: 0,
    overduePayables: 0,
    currentMonthRevenue: 0,
    currentMonthExpenses: 0,
    netIncome: 0,
    cashBalance: 0,
  };

  dashboardData: DashboardData = {
    totalReceivables: 0,
    outstandingInvoicesCount: 0,
    totalPayables: 0,
    outstandingBillsCount: 0,
    overdueInvoicesAmount: 0,
    overdueInvoicesCount: 0,
    billsDueSoonAmount: 0,
    billsDueSoonCount: 0,
    currentMonthRevenue: 0,
    currentMonthExpenses: 0,
    netIncome: 0,
    cashBalance: 0,
  };

  isLoading = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  // Get service stores
  get invoicesStore(): ServiceStore {
    return this.rootStore.invoicesStore as ServiceStore;
  }

  get billsStore(): ServiceStore {
    return this.rootStore.billsStore as ServiceStore;
  }

  get paymentsStore(): ServiceStore {
    return this.rootStore.paymentsStore as ServiceStore;
  }

  get customersStore(): ServiceStore {
    return this.rootStore.customersStore as ServiceStore;
  }

  get suppliersStore(): ServiceStore {
    return this.rootStore.suppliersStore as ServiceStore;
  }

  // Summary data methods
  setSummary = (summary: Partial<AccountingSummary>) => {
    this.summary = { ...this.summary, ...summary };
  };

  setDashboardData = (data: Partial<DashboardData>) => {
    this.dashboardData = { ...this.dashboardData, ...data };
  };

  // Load dashboard-specific data
  loadDashboardData = async (): Promise<void> => {
    try {
      this.isLoading = true;

      // Load entities from service stores
      await Promise.all([
        this.invoicesStore.find(),
        this.billsStore.find(),
        this.paymentsStore.find(),
      ]);

      // Calculate dashboard data from existing service stores
      await this.calculateDashboardData();

      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
    }
  };

  // Load method for ScheduleGrid compatibility
  load = async (): Promise<void> => {
    try {
      this.isLoading = true;

      // Load entities from service stores
      await Promise.all([
        this.invoicesStore.find(),
        this.billsStore.find(),
        this.paymentsStore.find(),
      ]);

      // Load and calculate dashboard data from service stores
      await this.calculateDashboardData();

      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
    }
  };

  // Calculate dashboard data from stores (fallback method)
  private calculateDashboardData = async (): Promise<void> => {
    try {
      // Calculate basic dashboard metrics from service store data
      const dashboardData: Partial<DashboardData> = {
        totalReceivables: this.calculateTotalReceivables(),
        outstandingInvoicesCount: this.calculateOutstandingInvoicesCount(),
        totalPayables: this.calculateTotalPayables(),
        outstandingBillsCount: this.calculateOutstandingBillsCount(),
        overdueInvoicesAmount: this.calculateOverdueInvoicesAmount(),
        overdueInvoicesCount: this.calculateOverdueInvoicesCount(),
        billsDueSoonAmount: this.calculateBillsDueSoonAmount(),
        billsDueSoonCount: this.calculateBillsDueSoonCount(),
        currentMonthRevenue: this.calculateCurrentMonthRevenue(),
        currentMonthExpenses: this.calculateCurrentMonthExpenses(),
        netIncome: this.calculateNetIncome(),
        cashBalance: this.calculateCashBalance(),
      };

      this.setDashboardData(dashboardData);

      // Also calculate and set summary data
      const summaryData: Partial<AccountingSummary> = {
        totalReceivables: this.calculateTotalReceivables(),
        totalPayables: this.calculateTotalPayables(),
        overdueReceivables: this.calculateOverdueInvoicesAmount(),
        overduePayables: this.calculateBillsDueSoonAmount(),
        currentMonthRevenue: this.calculateCurrentMonthRevenue(),
        currentMonthExpenses: this.calculateCurrentMonthExpenses(),
        netIncome: this.calculateNetIncome(),
        cashBalance: this.calculateCashBalance(),
      };

      this.setSummary(summaryData);
    } catch (error) {
      // Error calculating dashboard data
    }
  };

  // Helper calculation methods
  private calculateTotalReceivables = (): number => {
    const invoices =
      (this.invoicesStore.entities as unknown as Invoice[]) || [];
    return invoices
      .filter((invoice: Invoice) => invoice.status !== InvoiceStatus.PAID)
      .reduce(
        (sum: number, invoice: Invoice) => sum + (invoice.totalAmount || 0),
        0
      );
  };

  private calculateOutstandingInvoicesCount = (): number => {
    const invoices =
      (this.invoicesStore.entities as unknown as Invoice[]) || [];
    return invoices.filter(
      (invoice: Invoice) =>
        invoice.status === InvoiceStatus.SENT ||
        invoice.status === InvoiceStatus.OVERDUE
    ).length;
  };

  private calculateTotalPayables = (): number => {
    const bills = (this.billsStore.entities as unknown as Bill[]) || [];
    return bills
      .filter((bill: Bill) => bill.status !== BillStatus.PAID)
      .reduce((sum: number, bill: Bill) => sum + (bill.totalAmount || 0), 0);
  };

  private calculateOutstandingBillsCount = (): number => {
    const bills = (this.billsStore.entities as unknown as Bill[]) || [];
    return bills.filter(
      (bill: Bill) =>
        bill.status === BillStatus.RECEIVED ||
        bill.status === BillStatus.OVERDUE
    ).length;
  };

  private calculateOverdueInvoicesAmount = (): number => {
    const invoices =
      (this.invoicesStore.entities as unknown as Invoice[]) || [];
    const today = new Date().toISOString().split("T")[0];
    return invoices
      .filter(
        (invoice: Invoice) =>
          invoice.status !== InvoiceStatus.PAID && invoice.dueDate < today
      )
      .reduce(
        (sum: number, invoice: Invoice) => sum + (invoice.totalAmount || 0),
        0
      );
  };

  private calculateOverdueInvoicesCount = (): number => {
    const invoices =
      (this.invoicesStore.entities as unknown as Invoice[]) || [];
    const today = new Date().toISOString().split("T")[0];
    return invoices.filter(
      (invoice: Invoice) =>
        invoice.status !== InvoiceStatus.PAID && invoice.dueDate < today
    ).length;
  };

  private calculateBillsDueSoonAmount = (): number => {
    const bills = (this.billsStore.entities as unknown as Bill[]) || [];
    const today = new Date().toISOString().split("T")[0];
    return bills
      .filter(
        (bill: Bill) => bill.status !== BillStatus.PAID && bill.dueDate < today
      )
      .reduce((sum: number, bill: Bill) => sum + (bill.totalAmount || 0), 0);
  };

  private calculateBillsDueSoonCount = (): number => {
    const bills = (this.billsStore.entities as unknown as Bill[]) || [];
    const today = new Date().toISOString().split("T")[0];
    return bills.filter(
      (bill: Bill) => bill.status !== BillStatus.PAID && bill.dueDate < today
    ).length;
  };

  private calculateCurrentMonthRevenue = (): number => {
    const payments =
      (this.paymentsStore.entities as unknown as Payment[]) || [];
    const thisMonth = new Date().toISOString().substring(0, 7);
    return payments
      .filter(
        (payment: Payment) =>
          payment.type === "received" &&
          payment.paymentDate.startsWith(thisMonth)
      )
      .reduce(
        (sum: number, payment: Payment) => sum + (payment.amount || 0),
        0
      );
  };

  private calculateCurrentMonthExpenses = (): number => {
    const payments =
      (this.paymentsStore.entities as unknown as Payment[]) || [];
    const thisMonth = new Date().toISOString().substring(0, 7);
    return payments
      .filter(
        (payment: Payment) =>
          payment.type === "sent" && payment.paymentDate.startsWith(thisMonth)
      )
      .reduce(
        (sum: number, payment: Payment) => sum + (payment.amount || 0),
        0
      );
  };

  private calculateNetIncome = (): number => {
    return (
      this.calculateCurrentMonthRevenue() - this.calculateCurrentMonthExpenses()
    );
  };

  private calculateCashBalance = (): number => {
    const payments =
      (this.paymentsStore.entities as unknown as Payment[]) || [];
    const received = payments
      .filter((payment: Payment) => payment.type === "received")
      .reduce(
        (sum: number, payment: Payment) => sum + (payment.amount || 0),
        0
      );

    const sent = payments
      .filter((payment: Payment) => payment.type === "sent")
      .reduce(
        (sum: number, payment: Payment) => sum + (payment.amount || 0),
        0
      );

    return received - sent;
  };

  // Utility methods
  formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Calculate upcoming due dates for ScheduleGrid
  get upcomingDueDates() {
    // This would be computed from invoices and bills
    // For now, return mock data structure
    return [];
  }

  // Cash flow data for chart (last 30 days)
  get cashFlowData() {
    const payments =
      (this.paymentsStore.entities as unknown as Payment[]) || [];

    // Generate array of last 30 days using shared date utilities
    const todayStr = databaseDate();
    const thirtyDaysAgoStr = getOffsetDate(todayStr, { offset: -29 });
    const dates = getDateRange(thirtyDaysAgoStr, todayStr);

    const dailyData: {
      [key: string]: { received: number; sent: number; net: number };
    } = {};

    // Initialize daily data for all dates
    dates.forEach((dateStr) => {
      dailyData[dateStr] = { received: 0, sent: 0, net: 0 };
    });

    // Filter payments for the date range
    const recentPayments = payments.filter((payment) => {
      return dates.includes(payment.paymentDate);
    });

    // If we have payment data, use it
    if (recentPayments.length > 0) {
      recentPayments.forEach((payment) => {
        const dateStr = payment.paymentDate;
        if (dailyData[dateStr]) {
          if (payment.type === "received") {
            dailyData[dateStr].received += payment.amount;
          } else if (payment.type === "sent") {
            dailyData[dateStr].sent += payment.amount;
          }
        }
      });
    }

    // Prepare data for LineChart
    const receivedData = dates.map((date) => dailyData[date].received);
    const sentData = dates.map((date) => dailyData[date].sent);
    const netData = dates.map((date) => dailyData[date].net);

    // Convert dates to M/D format for x-axis
    // const xAxisData = dates.map((date) => renderDate(date));
    const xAxisData = dates.map((_, index) => index + 1);

    return {
      xAxisData,
      series: [
        {
          label: "Received",
          data: receivedData,
          color: "#4caf50", // Green for received money
        },
        {
          label: "Sent",
          data: sentData,
          color: "#f44336", // Red for sent money
        },
        {
          label: "Net Cash Flow",
          data: netData,
          color: "#2196f3", // Blue for net cash flow
        },
      ],
    };
  }

  // ScheduleGrid entities - combines invoices and bills for due dates view
  get dueDatesEntities() {
    const invoices =
      (this.invoicesStore.entities as unknown as Invoice[]) || [];
    const bills = (this.billsStore.entities as unknown as Bill[]) || [];

    const dueDateItems: Array<{
      id: string | number;
      type: "Invoice" | "Bill";
      number: string;
      customerSupplier: string;
      amount: number;
      dueDate: string;
      status: string;
    }> = [];

    // Add invoices
    invoices
      .filter(
        (invoice) => invoice.status !== InvoiceStatus.PAID && invoice.dueDate
      )
      .forEach((invoice) => {
        const daysUntilDue = Math.ceil(
          (new Date(invoice.dueDate).getTime() - new Date().getTime()) /
            (1000 * 60 * 60 * 24)
        );
        let status = "outstanding";
        if (daysUntilDue < 0) status = "overdue";
        else if (daysUntilDue <= 7) status = "due-soon";

        dueDateItems.push({
          id: invoice.id,
          type: "Invoice",
          number: invoice.invoiceNumber || "",
          customerSupplier: invoice.customerName || "",
          amount: invoice.totalAmount || 0,
          dueDate: invoice.dueDate,
          status,
        });
      });

    // Add bills
    bills
      .filter((bill) => bill.status !== BillStatus.PAID && bill.dueDate)
      .forEach((bill) => {
        const daysUntilDue = Math.ceil(
          (new Date(bill.dueDate).getTime() - new Date().getTime()) /
            (1000 * 60 * 60 * 24)
        );
        let status = "outstanding";
        if (daysUntilDue < 0) status = "overdue";
        else if (daysUntilDue <= 7) status = "due-soon";

        dueDateItems.push({
          id: bill.id,
          type: "Bill",
          number: bill.billNumber || "",
          customerSupplier: bill.supplierName || "",
          amount: bill.totalAmount || 0,
          dueDate: bill.dueDate,
          status,
        });
      });

    return dueDateItems.sort(
      (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
    );
  }

  // Pending approvals for dashboard
  get pendingApprovals(): PendingApprovalItem[] {
    const invoices =
      (this.invoicesStore.entities as unknown as Invoice[]) || [];
    const bills = (this.billsStore.entities as unknown as Bill[]) || [];

    const approvalItems: PendingApprovalItem[] = [];

    // Add invoices pending approval (status = DRAFT or similar pending state)
    invoices
      .filter((invoice) => invoice.status === InvoiceStatus.DRAFT)
      .forEach((invoice) => {
        const invoiceAny = invoice as any;
        approvalItems.push({
          id: `inv-${invoice.id}`,
          type: "invoice",
          number: invoice.invoiceNumber || `INV-${invoice.id}`,
          description: invoiceAny.description || invoiceAny.notes || "Invoice",
          amount: invoice.totalAmount || 0,
          date: invoice.invoiceDate || invoiceAny.created_at || "",
          vendorOrCustomer: invoice.customerName || "Unknown Customer",
          plantCode: invoiceAny.plantCode,
          hasAttachments: invoiceAny.attachments?.length > 0 || false,
          attachmentCount: invoiceAny.attachments?.length || 0,
          submittedBy: invoiceAny.createdBy || "Unknown",
          submittedAt: invoiceAny.created_at || new Date().toISOString(),
          priority: this.getApprovalPriority(invoice.totalAmount || 0),
        });
      });

    // Add bills pending approval (status = RECEIVED)
    bills
      .filter((bill) => bill.status === BillStatus.RECEIVED)
      .forEach((bill) => {
        const billAny = bill as any;
        approvalItems.push({
          id: `bill-${bill.id}`,
          type: "bill",
          number: bill.billNumber || `BILL-${bill.id}`,
          description: billAny.description || billAny.notes || "Bill",
          amount: bill.totalAmount || 0,
          date: bill.billDate || billAny.created_at || "",
          vendorOrCustomer: bill.supplierName || "Unknown Supplier",
          plantCode: billAny.plantCode,
          hasAttachments: billAny.attachments?.length > 0 || false,
          attachmentCount: billAny.attachments?.length || 0,
          submittedBy: billAny.createdBy || "Unknown",
          submittedAt: billAny.created_at || new Date().toISOString(),
          priority: this.getApprovalPriority(bill.totalAmount || 0),
        });
      });

    // Sort by priority (urgent first) then by date
    return approvalItems.sort((a, b) => {
      const priorityOrder = { urgent: 0, high: 1, normal: 2 };
      if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      }
      return new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime();
    });
  }

  // Determine approval priority based on amount
  private getApprovalPriority(amount: number): "normal" | "high" | "urgent" {
    if (amount >= 50000) return "urgent";
    if (amount >= 10000) return "high";
    return "normal";
  }

  // Approve an item
  approveItem = async (itemId: string, notes?: string): Promise<void> => {
    // Extract type and id from composite id
    const [type, id] = itemId.split("-");

    if (type === "inv") {
      // Update invoice status to SENT (approved)
      const invoice = (this.invoicesStore.entities as unknown as Invoice[])
        .find((i) => String(i.id) === id);
      if (invoice) {
        await this.invoicesStore.patch(id, {
          status: "SENT",
          approvedAt: new Date().toISOString(),
          ...(notes ? { approvalNotes: notes } : {}),
        });
      }
    } else if (type === "bill") {
      // Update bill status to APPROVED
      const bill = (this.billsStore.entities as unknown as Bill[])
        .find((b) => String(b.id) === id);
      if (bill) {
        await this.billsStore.patch(id, {
          status: "APPROVED",
          approvedAt: new Date().toISOString(),
          ...(notes ? { approvalNotes: notes } : {}),
        });
      }
    }
  };

  // Reject an item
  rejectItem = async (itemId: string, reason: string): Promise<void> => {
    const [type, id] = itemId.split("-");

    if (type === "inv") {
      await this.invoicesStore.patch(id, {
        status: "REJECTED",
        rejectionReason: reason,
        rejectedAt: new Date().toISOString(),
      });
    } else if (type === "bill") {
      await this.billsStore.patch(id, {
        status: "REJECTED",
        rejectionReason: reason,
        rejectedAt: new Date().toISOString(),
      });
    }
  };

  // Date range for ScheduleGrid
  get dateRange() {
    const entities = this.dueDatesEntities;
    if (entities.length === 0) return [];

    const dates = entities
      .map((entity) => entity.dueDate)
      .filter((date) => date && date !== "1970-01-01")
      .sort();

    if (dates.length === 0) return [];

    const startDate = dates[0];
    const endDate = dates[dates.length - 1];

    return getDateRange(startDate, endDate, { weekdays: true });
  }

  // Column headers for ScheduleGrid
  get columnHeaders() {
    // Group transactions by type for column headers
    const transactionTypes = ["Invoice", "Bill"];

    const headersByType = transactionTypes.map((type) => ({
      width: 200,
      title: `${type}s`,
      key: type, // Use exact case to match our data
      headerKey: "type", // This tells ScheduleGrid which field to filter by
      component: AccountingScheduleRightHeaderCell,
      bodyComponent: AccountingScheduleRightBodyCell,
    }));

    return [
      {
        width: 130,
        title: "Due Date",
        key: "dueDate",
        component: AccountingScheduleLeftHeaderCell,
        bodyComponent: AccountingScheduleLeftBodyCell,
      },
      ...headersByType,
    ];
  }
}

export default AccountingDashboardStore;
